/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-imports */
import { ComponentsOverrides } from '@mui/material/styles';
import {
  CardProps as MuiCardProps,
  generateUtilityClasses,
  getCardUtilityClass,
  getCardMediaUtilityClass,
  cardClasses as muiCardClasses,
  cardMediaClasses as muiCardMediaClasses,
} from '@mui/material';
import '../paper/types';
import { CSSProperties } from 'react';
import { projectPrefix } from '../constants';

export const cardMediaClasses = {
  ...muiCardMediaClasses,
  wrapper: getCardMediaUtilityClass('wrapper'),
};

export const miniCardClasses = {
  ...muiCardClasses,
  inputCheckbox: getCardUtilityClass('inputCheckbox'),
  inputRadio: getCardUtilityClass('inputRadio'),
  inputNone: getCardUtilityClass('inputNone'),
  brand: getCardUtilityClass('brand'),
  disabled: getCardUtilityClass('disabled'),
};

export interface MiniCardProps extends MuiCardProps {
  /**
   * General style for whole card
   * @default outlined
   */
  variant?: 'outlined' | 'brand' | 'elevation';
  /**
   * What kind of input is present in the card
   * @default null
   */
  input: 'radio' | 'checkbox' | 'none';
  /**
   * If true, the component is disabled.
   * Remember to also disable interactive components (buttons, links) inside the card.
   * @default false
   */
  disabled?: boolean;
}

export const cardClasses = {
  ...muiCardClasses,
  brand: getCardUtilityClass('brand'),
  subtle: getCardUtilityClass('subtle'),
  fullHeight: getCardUtilityClass('fullHeight'),
  interactive: getCardUtilityClass('interactive'),
  disabled: getCardUtilityClass('disabled'),
  horizontal: getCardUtilityClass('horizontal'),

  radiusDefault: getCardUtilityClass('radiusDefault'),
  radiusBrand: getCardUtilityClass('radiusBrand'),
  radiusNone: getCardUtilityClass('radiusNone'),

  mediaPad: getCardUtilityClass('mediaPad'),
  mediaStretch: getCardUtilityClass('mediaStretch'),

  gapExtraSmall: getCardUtilityClass('gapExtraSmall'),
  gapSmall: getCardUtilityClass('gapSmall'),
  gapMedium: getCardUtilityClass('gapMedium'),
  gapLarge: getCardUtilityClass('gapLarge'),

  paddingSmall: getCardUtilityClass('paddingSmall'),
  paddingMedium: getCardUtilityClass('paddingMedium'),
  paddingLarge: getCardUtilityClass('paddingLarge'),
  paddingExtraLarge: getCardUtilityClass('paddingExtraLarge'),

  divider: getCardUtilityClass('divider'),
};

export interface CardProps extends Omit<MuiCardProps, 'variant' | 'elevation' | 'raised' | 'square'> {
  /**
   * General style for whole card
   * @default outlined
   */
  variant?: 'outlined' | 'brand' | 'elevation' | 'subtle';
  /**
   * Border radius for underlying Paper
   * @default default
   */
  radius?: 'default' | 'brand' | 'none';
  /**
   * Display a divider after the card image
   * @default true
   */
  divider? : boolean
  /**
   * Control the gap size inside the Card
   * @default medium
   */
  gap?: 'medium' | 'extraSmall' | 'small' | 'large';
  /**
   * Control the padding size of the Card
   * @default medium
   */
  padding?: 'medium' | 'extraLarge' | 'small' | 'large';
  /**
   * Control whether CardMedia component should have padding or be stretched
   * to full width
   * @default pad
   */
  media?: 'pad' | 'stretch';
  /**
   * When set true, card will have 100% height
   * @default true
   */
  fullHeight?: boolean;
  /**
   * Setting to true will add change styles to be applicable for horizontal Cards
   * @default false
   */
  horizontal?: boolean;
  /**
   * If true, the component is disabled.
   * Remember to also disable interactive components (buttons, links) inside the card.
   * @default false
   */
  disabled?: boolean;
  /**
   * Setting to true will add hover and active states,
   * same states styles will apply when component is set to MuiButtonBase
   * @default false
   */
  interactive?: boolean;
  /**
   * Allows you to override as which component the Card is rendered as.
   * If you need the Card as a whole to be interactive (clickable), we recommend you set this to
   * @link https://mui.com/material-ui/api/button-base/ MuiButtonBase
   */
  component?: 'div' | undefined;
}

export const selectableCardClasses = generateUtilityClasses(
  `${projectPrefix}SelectableCard`,
  [
    'root',
    'fullHeight',
    'selected',
    'selectedIcon',
    'disabled',
    'brand',
    'elevation',
    'outlined',
  ],
);

export interface SelectableCardProps extends Omit<CardProps, 'interactive'> {
  /**
   * This will control application of proper styles, used by SelectableCard
   * @default undefined
   */
  selected?: boolean;
  /**
   * Props for div element wrapping Card components used inside
   */
  wrapperProps?: React.HTMLAttributes<HTMLDivElement> & { sx: CSSProperties };
  /**
   * Props for element wrapping check icon
   */
  iconProps?: React.HTMLAttributes<HTMLDivElement> & { sx: CSSProperties };
}

export interface OriginSelectableCard {
  styleOverrides?: ComponentsOverrides['MuiCard'];
  // TODO: defaultProps?: Partial<SelectableCardProps>;
}
